import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode'; // Import jwt-decode
// Import fetchUserData to make the verification call
import { fetchUserData } from '../utils/api';

// Create the context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState(null);
  const [userId, setUserId] = useState(null); // Add userId state
  const [isLoading, setIsLoading] = useState(true); // Loading state is crucial now
  const navigate = useNavigate();

  // Logout function needs to be defined before useEffect uses it
  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    localStorage.removeItem('userId'); // Clear userId from storage
    setIsAuthenticated(false);
    setUsername(null);
    setUserId(null); // Clear userId from state
    // Redirect to login page
    navigate('/login');
  }, [navigate]);

  // Check localStorage AND verify token with backend on initial load
  useEffect(() => {
    const verifyAuthStatus = async () => {
      const storedToken = localStorage.getItem('accessToken');
      if (storedToken) {
        try {
          await fetchUserData();
          const decodedToken = jwtDecode(storedToken);
          setIsAuthenticated(true);
          setUsername(decodedToken.username);
          setUserId(decodedToken.userId);
        } catch (error) {
          console.error('[AuthContext] Token verification failed, logging out:', error);
          logout();
        }
      }
      setIsLoading(false); // Finished check/verification
    };

    verifyAuthStatus();
  }, [logout]); // Include logout in dependency array as it's used inside

  // Login function remains the same
  const login = useCallback((newUsername, token, refreshToken) => {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('username', newUsername); // Still useful for quick display name access

    const decodedToken = jwtDecode(token);
    localStorage.setItem('userId', decodedToken.userId); // Store userId

    setIsAuthenticated(true);
    setUsername(newUsername);
    setUserId(decodedToken.userId); // Set userId in state
  }, []);

  // Value provided by the context
  const value = {
    isAuthenticated,
    username,
    userId, // Expose userId
    isLoading, // Provide loading state
    login,
    logout,
  };

  // Render children only after initial loading is potentially complete
  // Or let consumers decide based on isLoading
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Export the context itself (optional, as useAuth hook is preferred)
// export default AuthContext;
